import React from 'react';
import {
  Clock, ArrowRight, ShieldCheck, CheckCircle2, XCircle,
  Archive, AlertTriangle, Flag, FileText, Download,
} from 'lucide-react';
import { getStatusMeta, getPriorityMeta } from '../utils/helpers';

export function StatusBadge({ status }) {
  const meta = getStatusMeta(status);
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${meta.color}`}>
      <Icon className="w-3 h-3 mr-1" />{meta.label}
    </span>
  );
}

export function PriorityBadge({ priority }) {
  const meta = getPriorityMeta(priority);
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${meta.color}`}>
      <Flag className="w-3 h-3 mr-1" />{meta.label}
    </span>
  );
}

export function RiskBadge({ level }) {
  const colors = {
    high: 'bg-rose-100 text-rose-700 border-rose-200',
    medium: 'bg-amber-100 text-amber-700 border-amber-200',
    low: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${colors[level] || colors.low}`}>
      {level === 'high' ? '高风险' : level === 'medium' ? '中风险' : '低风险'}
    </span>
  );
}

export function SectionTitle({ icon: Icon, title }) {
  return (
    <div className="flex items-center space-x-2 text-sm font-semibold text-slate-700 border-b border-slate-100 pb-2">
      <Icon className="w-4 h-4 text-indigo-500" /><span>{title}</span>
    </div>
  );
}

export function FormInput({ label, value, onChange, type = 'text', placeholder, readOnly = false }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
      <input
        type={type}
        readOnly={readOnly}
        className={`w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${readOnly ? 'bg-slate-50 text-slate-500' : ''}`}
        placeholder={placeholder}
        value={value}
        onChange={e => onChange(e.target.value)}
      />
    </div>
  );
}

export function FormSelect({ label, value, onChange, options, labels = {}, readOnly = false }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
      <select
        disabled={readOnly}
        className={`w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white ${readOnly ? 'bg-slate-50 text-slate-500' : ''}`}
        value={value}
        onChange={e => onChange(e.target.value)}
      >
        {options.map(o => <option key={o} value={o}>{labels[o] || o}</option>)}
      </select>
    </div>
  );
}

export function FormTextArea({ label, value, onChange, placeholder, rows = 3, readOnly = false }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
      <textarea
        readOnly={readOnly}
        className={`w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-y ${readOnly ? 'bg-slate-50 text-slate-500' : ''}`}
        rows={rows}
        placeholder={placeholder}
        value={value}
        onChange={e => onChange(e.target.value)}
      />
    </div>
  );
}

export function FilterSelect({ label, value, onChange, options, labels = {} }) {
  return (
    <div>
      <label className="block text-xs text-slate-500 mb-1">{label}</label>
      <select className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white" value={value} onChange={e => onChange(e.target.value)}>
        {options.map(o => <option key={o} value={o}>{labels[o] || o || '全部'}</option>)}
      </select>
    </div>
  );
}

export function InfoItem({ label, value }) {
  return (
    <div>
      <div className="text-xs text-slate-400 mb-0.5">{label}</div>
      <div className="text-sm text-slate-800">{value}</div>
    </div>
  );
}

export function FileList({ files }) {
  if (!files || files.length === 0) return <div className="text-sm text-slate-400">暂无附件</div>;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {files.map((f, i) => (
        <div key={i} className="flex items-center p-3 border border-slate-200 rounded-lg hover:bg-slate-50">
          <FileText className="w-8 h-8 text-indigo-500 mr-3" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-slate-800 truncate">{f.name}</div>
            <div className="text-xs text-slate-400">{f.size}</div>
          </div>
          <button className="p-2 text-slate-400 hover:text-indigo-600" onClick={() => alert('下载：' + f.name)}>
            <Download className="w-4 h-4" />
          </button>
        </div>
      ))}
    </div>
  );
}
